import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import AdminLayout from '../../components/AdminLayuot'; 
import { useNavigate } from 'react-router-dom';

const THEME = {
    primary: '#1F2937', secondary: '#3B82F6', success: '#10B981',
    warning: '#F59E0B', danger: '#EF4444', bg: '#F3F4F6',
    white: '#FFFFFF', border: '#E5E7EB'
};

const STATUS_OPTIONS = [
    { value: 'SOLICITADO', label: '⏳ Nuevo', color: '#F59E0B' },
    { value: 'CONFIRMADO', label: '✅ Confirmado', color: '#3B82F6' },
    { value: 'EN_RUTA', label: '🚚 En Ruta', color: '#8B5CF6' },
    { value: 'ENTREGADO', label: '🏁 Entregado', color: '#10B981' },
    { value: 'CANCELADO', label: '❌ Cancelado', color: '#EF4444' }
];

function AdminPedidos() {
    const navigate = useNavigate();
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('TODOS');
    const [processingId, setProcessingId] = useState(null);

    // --- FILTROS ---
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [selectedClient, setSelectedClient] = useState('TODOS');

    const formatearFechaFija = (fechaISO) => {
        if (!fechaISO) return '-';
        const partes = fechaISO.split('T')[0].split('-'); 
        return `${partes[2]}/${partes[1]}/${partes[0]}`; 
    };

    const fetchPedidos = async () => {
        const token = localStorage.getItem('token');
        try {
            const { data } = await api.get('/pedidos?limit=200', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const lista = data.data || [];
            setPedidos(lista.sort((a, b) => {
                if (a.estado === 'SOLICITADO' && b.estado !== 'SOLICITADO') return -1;
                if (a.estado !== 'SOLICITADO' && b.estado === 'SOLICITADO') return 1;
                return new Date(b.createdAt) - new Date(a.createdAt);
            }));
        } catch (error) { console.error(error); } finally { setLoading(false); }
    };

    useEffect(() => { fetchPedidos(); }, []);

    const listaClientes = [...new Set(pedidos.map(p => p.usuario?.username).filter(Boolean))].sort();

    // --- LÓGICA DE CAMBIO DE ESTADO ACTUALIZADA ---
    const handleStatusChange = async (id, newStatus) => {
        let recibidoPor = null;

        // Si el nuevo estado es ENTREGADO, pedimos el nombre antes de confirmar
        if (newStatus === 'ENTREGADO') {
            const nombre = window.prompt("¿Quién recibió el pedido? (Nombre completo)");
            if (!nombre || nombre.trim() === "") {
                alert("El nombre de quien recibe es obligatorio para marcar como entregado.");
                fetchPedidos(); // Resetear el select
                return;
            }
            recibidoPor = nombre;
        } else {
            const confirmMsg = newStatus === 'CANCELADO' ? "¿Cancelar pedido?" : `¿Cambiar a ${newStatus}?`;
            if (!window.confirm(confirmMsg)) { fetchPedidos(); return; }
        }
        
        setProcessingId(id);
        const token = localStorage.getItem('token');
        try {
            const payload = { estado: newStatus };
            if (recibidoPor) payload.recibidoPor = recibidoPor; // Inyectamos el nombre si existe

            await api.patch(`/pedidos/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await fetchPedidos();
        } catch (error) { 
            alert("Error al actualizar el pedido."); 
        } finally { 
            setProcessingId(null); 
        }
    };

    // --- LÓGICA DE FILTRADO COMBINADA (Mantenida intacta) ---
    const filteredPedidos = pedidos.filter(p => {
        let pasaEstado = true;
        if (filter === 'NUEVOS') pasaEstado = p.estado === 'SOLICITADO';
        else if (filter === 'ACTIVOS') pasaEstado = ['CONFIRMADO', 'EN_RUTA'].includes(p.estado);

        let pasaCliente = true;
        if (selectedClient !== 'TODOS' && p.usuario?.username !== selectedClient) pasaCliente = false;

        const fechaPedido = (p.fechaEntrega || p.createdAt).split('T')[0];
        let pasaFecha = true;
        if (fechaDesde && fechaPedido < fechaDesde) pasaFecha = false;
        if (fechaHasta && fechaPedido > fechaHasta) pasaFecha = false;

        return pasaEstado && pasaCliente && pasaFecha;
    });

    const thStyle = { padding: '15px', textAlign: 'left', borderBottom: `2px solid ${THEME.border}`, color: '#6B7280', fontSize: 12, textTransform: 'uppercase' };
    const tdStyle = { padding: '15px', borderBottom: `1px solid ${THEME.border}`, color: THEME.primary, fontSize: 14, verticalAlign: 'middle' };
    const inputStyle = { padding: '8px', borderRadius: '8px', border: `1px solid ${THEME.border}`, fontSize: '13px', outline: 'none', color: THEME.primary, background: '#fff' };
    const btnFilter = (f) => ({ padding: '8px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: 13, background: filter === f ? THEME.primary : THEME.white, color: filter === f ? THEME.white : '#6B7280', boxShadow: filter === f ? '0 4px 6px rgba(0,0,0,0.1)' : 'none' });

    if (loading) return <AdminLayout title="Pedidos">Cargando...</AdminLayout>;

    return (
        <AdminLayout title="Gestión de Pedidos">
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                
                {/* BARRA DE FILTROS AVANZADA (Mantenida intacta) */}
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '20px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#6B7280' }}>ESTADO</label>
                            <div style={{ display: 'flex', gap: 10, background: '#E5E7EB', padding: 5, borderRadius: 25 }}>
                                <button onClick={() => setFilter('TODOS')} style={btnFilter('TODOS')}>Todos</button>
                                <button onClick={() => setFilter('NUEVOS')} style={btnFilter('NUEVOS')}>🔔 Nuevos ({pedidos.filter(p=>p.estado==='SOLICITADO').length})</button>
                                <button onClick={() => setFilter('ACTIVOS')} style={btnFilter('ACTIVOS')}>🚚 Activos</button>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#6B7280' }}>CLIENTE</label>
                            <select 
                                value={selectedClient} 
                                onChange={(e) => setSelectedClient(e.target.value)}
                                style={{ ...inputStyle, minWidth: '180px' }}
                            >
                                <option value="TODOS">-- Todos los Clientes --</option>
                                {listaClientes.map(cliente => (
                                    <option key={cliente} value={cliente}>{cliente}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#6B7280' }}>DESDE</label>
                                <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={inputStyle} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '10px', fontWeight: 'bold', color: '#6B7280' }}>HASTA</label>
                                <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={inputStyle} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                onClick={() => { setFechaDesde(''); setFechaHasta(''); setSelectedClient('TODOS'); setFilter('TODOS'); }}
                                style={{ background: '#F3F4F6', color: '#6B7280', border: 'none', padding: '10px 15px', borderRadius: 8, cursor: 'pointer', fontSize: '13px' }}
                            >
                                Limpiar
                            </button>
                            <button onClick={() => navigate('/admin/solicitar-pedido')} style={{ background: THEME.success, color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer' }}>+ Nuevo</button>
                        </div>
                    </div>
                </div>

                {/* TABLA CON MEJORAS DE FOLIO Y RECEPTOR */}
                <div style={{ background: THEME.white, borderRadius: 12, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Folio / Fecha</th>
                                <th style={thStyle}>Cliente</th>
                                <th style={thStyle}>Producto</th>
                                <th style={thStyle}>Total / Pago</th>
                                <th style={thStyle}>Estado (Control)</th>
                                <th style={thStyle}>Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPedidos.map(p => (
                                <tr key={p.id} style={{ background: p.estado === 'SOLICITADO' ? '#FEF3C7' : 'white' }}>
                                    <td style={tdStyle}>
                                        <div style={{fontWeight:'bold', fontFamily:'monospace', color: p.folioRecibo ? THEME.secondary : THEME.primary}}>
                                            {p.folioRecibo || `...${p.id.slice(-6)}`}
                                        </div>
                                        <div style={{fontSize:12, color:'#6B7280'}}>{formatearFechaFija(p.fechaEntrega || p.createdAt)}</div>
                                    </td>
                                    <td style={tdStyle}>
                                        <div style={{fontWeight:'bold'}}>{p.sucursal?.nombre || 'Sin Sucursal'}</div>
                                        <div style={{fontSize:12, color:'#6B7280'}}>{p.usuario?.username || 'Cliente'}</div>
                                    </td>
                                    <td style={tdStyle}>
                                        <div>{p.tipoProducto?.replace(/_/g, ' ')}</div>
                                        <div style={{fontSize:12, color:'#6B7280'}}>{(Number(p.cantidadGrano) + Number(p.cantidadMolido))} Kg</div>
                                    </td>
                                    <td style={{...tdStyle, fontWeight:'bold', color: p.pagado ? THEME.success : THEME.danger}}>
                                        ${Number(p.montoTotal).toLocaleString('es-MX')}
                                        <div style={{fontSize:10, textTransform:'uppercase'}}>{p.pagado ? 'Pagado' : 'Pendiente'}</div>
                                        {p.recibidoPor && (
                                            <div style={{fontSize:10, color:'#6B7280', fontWeight:'normal', marginTop:4}}>
                                                👤 Recibió: {p.recibidoPor}
                                            </div>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        <select
                                            value={p.estado}
                                            onChange={(e) => handleStatusChange(p.id, e.target.value)}
                                            disabled={processingId === p.id}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: 8,
                                                border: `1px solid ${STATUS_OPTIONS.find(o => o.value === p.estado)?.color}`,
                                                color: STATUS_OPTIONS.find(o => o.value === p.estado)?.color,
                                                fontWeight: 'bold',
                                                background: `${STATUS_OPTIONS.find(o => o.value === p.estado)?.color}10`,
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                minWidth: 140
                                            }}
                                        >
                                            {STATUS_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={tdStyle}>
                                        <button onClick={() => navigate(`/admin/solicitudes/${p.id}`)} style={{ background: 'transparent', border: `1px solid ${THEME.border}`, color: '#6B7280', padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>👁️</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminPedidos;